/**
 * Store selector for local stores with multiple stock locations.
 * - mandatory modal on first visit (no store chosen yet)
 * - persistent floating button to switch store at any time
 * - cart-reset warning when switching with a non-empty cart
 * The chosen store is persisted in localStorage and mirrored to a cart attribute
 * so the PDP / checkout know the active stock location.
 */
class StoreSelector extends HTMLElement {
    static SELECTED_STORE_KEY = 'vlisco_selected_store';

    constructor() {
        super();
        this.dataElement = this.querySelector('[data-store-selector-data]');
        this.modal = this.querySelector('[data-store-modal]');
        this.selectView = this.querySelector('[data-store-select-view]');
        this.warningView = this.querySelector('[data-store-warning-view]');
        this.select = this.querySelector('[data-store-select]');
        this.overlay = this.querySelector('[data-store-overlay]');
        this.toggleButton = this.querySelector('[data-store-toggle]');
        this.confirmButton = this.querySelector('[data-store-confirm]');
        this.closeButton = this.querySelector('[data-store-close]');
        this.warningConfirm = this.querySelector('[data-store-warning-confirm]');
        this.warningCancel = this.querySelector('[data-store-warning-cancel]');
        this.pendingStoreId = null;
    }

    connectedCallback() {
        this.renderOptions();
        this.bindEvents();

        const storeId = (this.selectedStoreId || '').replace(/\D/g, '');
        const cartStore = (this.dataset.cartStore || '').replace(/\D/g, '');
        const cartTitle = this.dataset.cartStoreTitle || '';

        const handoffParams = new URLSearchParams(location.search);
        const isHandoff = handoffParams.has('vlisco_store') || handoffParams.has('vlisco_switch');
        const outOfSync = storeId !== cartStore || this.storeName(this.selectedStoreId) !== cartTitle;

        if (storeId && !isHandoff && outOfSync) {
            this.setCartAttribute(this.selectedStoreId);
        }

        if (this.locations.length > 1 && !this.selectedStoreId) {
            this.openMandatoryWhenCountryResolved();
        }
    }

    openMandatoryWhenCountryResolved() {
        const open = () => {
            if (!this.selectedStoreId) this.openModal({ mandatory: true });
        };

        if (window.vliscoCountryRedirectResolved || !document.getElementById('country-redirect-popup')) {
            open();
            return;
        }

        document.addEventListener('country-redirect:resolved', open, { once: true });
    }

    get locations() {
        try {
            return JSON.parse(this.dataElement.textContent) || [];
        } catch (error) {
            return [];
        }
    }

    get selectedStoreId() {
        return localStorage.getItem(StoreSelector.SELECTED_STORE_KEY) || '';
    }

    get defaultStoreId() {
        const fallback = this.locations.find((location) => location.default) || this.locations[0];

        return (fallback && fallback.id) || '';
    }

    storeName(id) {
        const target = String(id).replace(/\D/g, '');
        const match = this.locations.find((location) => String(location.id).replace(/\D/g, '') === target);

        return (match && match.name) || '';
    }

    renderOptions() {
        if (!this.select) return;

        const active = this.selectedStoreId || this.defaultStoreId;
        if (active) this.select.value = active;
    }

    bindEvents() {
        if (this.toggleButton) this.toggleButton.addEventListener('click', () => this.openModal({ mandatory: false }));
        if (this.closeButton) this.closeButton.addEventListener('click', () => this.closeModal());
        if (this.confirmButton) this.confirmButton.addEventListener('click', () => this.onConfirm());
        if (this.warningConfirm) this.warningConfirm.addEventListener('click', () => this.applySwitch(this.pendingStoreId, true));
        if (this.warningCancel) this.warningCancel.addEventListener('click', () => this.showSelectView());
        if (this.overlay) this.overlay.addEventListener('click', () => {
            if (this.selectedStoreId) this.closeModal();
        });

        document.addEventListener('store-selector:open', () => this.openModal({ mandatory: false }));
    }

    openModal({ mandatory }) {
        this.renderOptions();
        this.showSelectView();

        this.modal.dataset.mandatory = mandatory ? 'true' : 'false';
        if (this.closeButton) this.closeButton.style.display = mandatory ? 'none' : '';
        this.modal.style.display = 'block';
    }

    closeModal() {
        if (this.modal.dataset.mandatory === 'true') return;

        this.modal.style.display = 'none';
    }

    onConfirm() {
        const id = this.select.value;

        if (!id) return;

        if (id === this.selectedStoreId) {
            this.modal.dataset.mandatory = 'false';
            this.closeModal();

            return;
        }

        this.requestSwitch(id);
    }

    async requestSwitch(id) {
        const itemCount = await this.cartItemCount();

        if (itemCount > 0) {
            this.pendingStoreId = id;
            this.showWarningView();
        } else {
            this.applySwitch(id, false);
        }
    }

    async cartItemCount() {
        try {
            const cart = await fetch('/cart.js').then(res => res.json());

            return cart.item_count || 0;
        } catch (error) {
            return 0;
        }
    }

    showWarningView() {
        if (this.selectView) this.selectView.hidden = true;
        if (this.warningView) this.warningView.hidden = false;
    }

    showSelectView() {
        if (this.warningView) this.warningView.hidden = true;
        if (this.selectView) this.selectView.hidden = false;
    }

    async applySwitch(id, resetCart) {
        if (!id) return;

        if (resetCart) await this.clearCart();

        localStorage.setItem(StoreSelector.SELECTED_STORE_KEY, id);
        await this.setCartAttribute(id);

        this.reloadForStore(id);
    }

    reloadForStore(id) {
        const numericId = String(id).replace(/\D/g, '');
        const isProductListing =
            (/\/collections\//.test(location.pathname) && !/\/products\//.test(location.pathname)) ||
            location.pathname.startsWith('/search');

        if (isProductListing) {
            const url = new URL(location.href);

            url.searchParams.set(window.VLISCO_STORE_FILTER_PARAM, numericId);
            location.assign(url.toString());
        } else {
            location.reload();
        }
    }

    async clearCart() {
        try {
            await fetch('/cart/clear.js', { method: 'POST' });
        } catch (error) {
            console.error(error);
        }
    }

    async setCartAttribute(id) {
        try {
            await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    attributes: {
                        selected_store: id,
                        selected_store_title: this.storeName(id)
                    }
                })
            });
        } catch (error) {
            console.error(error);
        }
    }
}

customElements.define('store-selector', StoreSelector);
